import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, CheckCircle2, Clock, MessageSquare, Megaphone, Ticket, UserPlus, Users } from 'lucide-react';
import { useAdminApi, errorMessage } from './adminApi';
import { formatDate, formatRelative, formatTime, formatWeekday } from './format';
import { EmptyState, Panel, PriorityBadge, Spinner, btnDanger, btnPrimary, btnSecondary } from './AdminUI';

const ACTIVITY_FILTERS = [
  { value: '', label: 'All' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'forum', label: 'Forum' },
  { value: 'bulletins', label: 'Bulletins' },
  { value: 'residents', label: 'Residents' }
];
const ACTIVITY_ICONS = { tickets: Ticket, forum: MessageSquare, bulletins: Megaphone, residents: UserPlus };
const SUBSCRIPTION_LABELS = [
  ['newBulletin', 'Bulletins'],
  ['newTicket', 'New tickets'],
  ['newForumPost', 'Forum posts'],
  ['newForumComment', 'Forum comments'],
  ['forumMention', 'Mentions']
];

function targetPath(slug, target) {
  const { tab, ...query } = target;
  const search = new URLSearchParams(query).toString();
  return `/admin/${slug}/${tab}${search ? `?${search}` : ''}`;
}

function Tile({ to, label, value, total, children }) {
  return (
    <Link to={to} className="block bg-card rounded-lg border border-border p-4 hover:border-primary transition-colors">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold text-foreground my-1">
        {value}
        {total !== undefined && <span className="text-lg font-medium text-muted-foreground"> / {total}</span>}
      </p>
      <p className="text-xs text-muted-foreground">{children}</p>
    </Link>
  );
}

function CardHeader({ title, count, to, linkLabel }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {count > 0 && <span className="min-w-6 px-2 rounded-full bg-notificationBadge text-notificationBadge-foreground text-xs text-center">{count}</span>}
      {to && <Link to={to} className="ml-auto text-sm text-primary hover:underline">{linkLabel} →</Link>}
    </div>
  );
}

function Row({ icon: Icon, children, actions }) {
  return (
    <li className="flex items-center gap-3 py-3 border-t border-border first:border-t-0">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">{children}</div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </li>
  );
}

function TicketTrend({ weeks }) {
  const [hover, setHover] = useState(null);
  const width = 360;
  const height = 160;
  const left = 24;
  const bottom = 24;
  const max = Math.max(4, ...weeks.flatMap((w) => [w.opened, w.closed]));
  const groupWidth = (width - left) / weeks.length;
  const barWidth = Math.min(10, (groupWidth - 6) / 2);
  const y = (v) => 6 + (height - bottom - 6) * (1 - v / max);
  const ticks = [0, Math.round(max / 2), max];

  const bar = (x, value, className) => {
    const h = y(0) - y(value);
    if (h <= 0) return null;
    const r = Math.min(3, h);
    return <path className={className} d={`M${x},${y(0)} v${-(h - r)} q0,${-r} ${r},${-r} h${barWidth - 2 * r} q${r},0 ${r},${r} v${h - r} z`} />;
  };

  return (
    <div className="relative">
      <div className="flex gap-4 text-xs text-muted-foreground mb-2">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-chart1" />Opened</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-chart2" />Closed</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Tickets opened and closed per week, last 12 weeks">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={left} x2={width} y1={y(t)} y2={y(t)} className="stroke-border" strokeWidth="1" />
            <text x={left - 6} y={y(t) + 5} textAnchor="end" className="fill-muted-foreground text-xs">{t}</text>
          </g>
        ))}
        {weeks.map((w, i) => {
          const x = left + i * groupWidth + (groupWidth - 2 * barWidth - 2) / 2;
          return (
            <g key={w.weekStart} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={left + i * groupWidth} y={0} width={groupWidth} height={height - bottom} className={hover === i ? 'fill-muted' : 'fill-transparent'} />
              {bar(x, w.opened, 'fill-chart1')}
              {bar(x + barWidth + 2, w.closed, 'fill-chart2')}
              {i % 3 === 0 && <text x={left + i * groupWidth + groupWidth / 2} y={height - 6} textAnchor="middle" className="fill-muted-foreground text-xs">{formatDate(w.weekStart)}</text>}
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <div className="absolute top-6 right-0 rounded border border-border bg-card px-3 py-2 text-xs shadow-lg pointer-events-none">
          <p className="text-muted-foreground mb-1">Week of {formatDate(weeks[hover].weekStart)}</p>
          <p className="text-foreground">Opened: {weeks[hover].opened}</p>
          <p className="text-foreground">Closed: {weeks[hover].closed}</p>
        </div>
      )}
      <table className="sr-only">
        <caption>Tickets per week</caption>
        <thead><tr><th>Week of</th><th>Opened</th><th>Closed</th></tr></thead>
        <tbody>{weeks.map((w) => <tr key={w.weekStart}><td>{formatDate(w.weekStart)}</td><td>{w.opened}</td><td>{w.closed}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

function DashboardTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [data, setData] = useState(null);
  const [activityFilter, setActivityFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const base = `/admin/${condo.slug}`;

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/api/admin/condos/${condo._id}/dashboard`);
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to load the dashboard'));
    }
  }, [api, condo._id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (registration, approve) => {
    if (!approve && !window.confirm(`Reject and delete the sign-up from "${registration.name}"?`)) return;
    setBusyId(registration._id);
    try {
      if (approve) await api.put(`/api/admin/users/${registration._id}`, { isActive: true });
      else await api.del(`/api/admin/users/${registration._id}`);
      notify(approve ? `${registration.name} approved.` : 'Sign-up rejected.');
      load();
      onCountsChange();
    } catch (err) {
      alert(errorMessage(err, approve ? 'Failed to approve user' : 'Failed to reject user'));
    } finally {
      setBusyId(null);
    }
  };

  if (!data) return <Spinner />;

  const { tiles, attention, upcoming, activity, recentResidents, ticketTrend, subscriptions } = data;
  const attentionCount = attention.approvals.length + attention.registrations.length + attention.staleTickets.length;
  const shownActivity = activity.filter((item) => !activityFilter || item.type === activityFilter);

  return (
    <div className="space-y-4">
      <section aria-label="At a glance" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Tile to={`${base}/tickets`} label="Open tickets" value={tiles.openTickets}>
          {tiles.urgentTickets > 0 && <span className="text-priorityUrgent">{tiles.urgentTickets} urgent · </span>}
          {tiles.oldestOpenDays !== null ? `oldest ${tiles.oldestOpenDays} days` : 'none open'}
        </Tile>
        <Tile to={`${base}/approvals`} label="Waiting for approval" value={tiles.pendingApprovals}>proposals to review</Tile>
        <Tile to={`${base}/residents`} label="Sign-ups to approve" value={tiles.pendingRegistrations}>
          {attention.registrations[0] ? `newest ${formatRelative(attention.registrations[0].createdAt).toLowerCase()}` : 'all caught up'}
        </Tile>
        <Tile to={`${base}/residents`} label="Active residents (7 days)" value={tiles.activeResidents7d} total={tiles.residents}>signed in this week</Tile>
        <Tile to={`${base}/units`} label="Units with a resident" value={tiles.occupiedUnits} total={tiles.units}>
          {tiles.units - tiles.occupiedUnits} with nobody signed up
        </Tile>
        <Tile to={`${base}/calendar`} label="Next 7 days" value={tiles.upcomingEvents}>
          event{tiles.upcomingEvents === 1 ? '' : 's'} · {tiles.upcomingBookings} amenity booking{tiles.upcomingBookings === 1 ? '' : 's'}
        </Tile>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel className="xl:col-span-2">
          <CardHeader title="Needs your attention" count={attentionCount} to={`${base}/approvals`} linkLabel="Approvals" />
          {attentionCount === 0 ? (
            <EmptyState><CheckCircle2 className="inline h-4 w-4 mr-1 text-success" />Nothing needs you right now.</EmptyState>
          ) : (
            <ul>
              {attention.approvals.map((a) => (
                <Row key={a._id} icon={CheckCircle2} actions={<Link to={`${base}/approvals`} className={btnPrimary}>Review</Link>}>
                  <p className="text-sm text-foreground truncate">{a.label}: “{a.title}”</p>
                  <p className="text-xs text-muted-foreground">Proposed {formatRelative(a.createdAt).toLowerCase()}</p>
                </Row>
              ))}
              {attention.registrations.map((r) => (
                <Row
                  key={r._id}
                  icon={UserPlus}
                  actions={<>
                    <button type="button" onClick={() => decide(r, true)} disabled={busyId === r._id} className={btnPrimary}>Approve</button>
                    <button type="button" onClick={() => decide(r, false)} disabled={busyId === r._id} className={btnDanger}>Reject</button>
                  </>}
                >
                  <p className="text-sm text-foreground truncate">{r.name} signed up{r.unit != null ? ` for unit ${r.unit}` : ''} <span className="capitalize text-muted-foreground">({r.role})</span></p>
                  <p className="text-xs text-muted-foreground">{r.email} · {formatRelative(r.createdAt).toLowerCase()}</p>
                </Row>
              ))}
              {attention.staleTickets.map((t) => (
                <Row key={t._id} icon={t.priority === 'urgent' ? AlertTriangle : Clock} actions={<><PriorityBadge priority={t.priority} /><Link to={`${base}/tickets?ticket=${t._id}`} className={btnSecondary}>Open</Link></>}>
                  <p className="text-sm text-foreground truncate">{[t.ticketNumber, t.title].filter(Boolean).join(' ')}</p>
                  <p className="text-xs text-muted-foreground">{t.idleDays > 0 ? `No update for ${t.idleDays} day${t.idleDays === 1 ? '' : 's'}` : 'Updated today'}</p>
                </Row>
              ))}
            </ul>
          )}
        </Panel>

        <Panel>
          <CardHeader title="Coming up · next 7 days" to={`${base}/calendar`} linkLabel="Calendar" />
          {upcoming.length === 0 ? <EmptyState>Nothing scheduled this week.</EmptyState> : (
            <ul>
              {upcoming.slice(0, 8).map((u) => (
                <Row key={`${u.kind}-${u._id}`} icon={u.kind === 'booking' ? Users : CalendarDays}>
                  <p className="text-sm text-foreground truncate">{u.title}{u.kind === 'booking' && u.unit != null ? `: unit ${u.unit}` : ''}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatWeekday(u.start)} {formatDate(u.start)} · {formatTime(u.start)}–{formatTime(u.end)}
                    {u.kind === 'booking' ? ' · amenity booking' : u.recurring ? ' · repeats' : ''}
                  </p>
                </Row>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Panel>
          <CardHeader title="Recent activity" />
          <div role="group" aria-label="Filter activity" className="flex flex-wrap gap-2 mb-2">
            {ACTIVITY_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={activityFilter === f.value}
                onClick={() => setActivityFilter(f.value)}
                className={`text-xs px-3 py-1 rounded-full border ${activityFilter === f.value ? 'bg-secondary text-foreground border-border' : 'text-muted-foreground border-border hover:text-foreground'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {shownActivity.length === 0 ? <EmptyState>No recent activity.</EmptyState> : (
            <ul>
              {shownActivity.slice(0, 10).map((item, i) => (
                <Row key={`${item.type}-${item.at}-${i}`} icon={ACTIVITY_ICONS[item.type] || Ticket}>
                  <Link to={targetPath(condo.slug, item.target)} className="block text-sm text-foreground truncate hover:text-primary">
                    {item.title}{item.unit != null ? ` (unit ${item.unit})` : ''}
                  </Link>
                  <p className="text-xs text-muted-foreground truncate">{formatRelative(item.at)}{item.detail ? ` · ${item.detail}` : ''}</p>
                </Row>
              ))}
            </ul>
          )}
        </Panel>

        <Panel>
          <CardHeader title="Recently active residents" to={`${base}/residents`} linkLabel="Residents" />
          {recentResidents.length === 0 ? <EmptyState>No one has signed in yet.</EmptyState> : (
            <ul>
              {recentResidents.map((r) => (
                <li key={r._id} className="flex items-center gap-3 py-2 border-t border-border first:border-t-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-sm font-medium text-primary">{(r.name || '?').charAt(0).toUpperCase()}</span>
                  <Link to={`${base}/residents?user=${r._id}`} className="min-w-0 flex-1 hover:text-primary">
                    <p className="text-sm text-foreground truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{r.unit != null ? `Unit ${r.unit} · ` : ''}{r.role}</p>
                  </Link>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatRelative(r.lastActive)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="lg:col-span-2 xl:col-span-1">
          <CardHeader title="Tickets per week · last 12 weeks" to={`${base}/tickets?status=all`} linkLabel="Tickets" />
          <TicketTrend weeks={ticketTrend} />
          <section aria-labelledby="admin-stats-heading" className="mt-5">
            <h2 id="admin-stats-heading" className="text-base font-semibold text-foreground mb-2">Stats</h2>
            <p className="text-xs text-muted-foreground mb-3">Tickets followed by active residents: <span className="text-foreground font-medium">{subscriptions.followedTickets}</span></p>
            <h3 className="text-sm font-medium text-foreground mb-2">Notification subscriptions</h3>
            <dl className="space-y-2">
              {SUBSCRIPTION_LABELS.map(([key, label]) => {
                const count = subscriptions.counts[key] || 0;
                const share = subscriptions.total ? Math.round((count / subscriptions.total) * 100) : 0;
                return (
                  <div key={key} className="grid grid-cols-3 items-center gap-2 text-xs">
                    <dt className="text-foreground">{label}</dt>
                    <dd className="h-2 rounded-full bg-secondary overflow-hidden" aria-hidden="true"><div className="h-full rounded-full bg-chart1" style={{ width: `${share}%` }} /></dd>
                    <dd className="text-muted-foreground text-right">{count} / {subscriptions.total}</dd>
                  </div>
                );
              })}
            </dl>
          </section>
        </Panel>
      </div>
    </div>
  );
}

export default DashboardTab;
